import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Proptypes from "prop-types";
import { FieldArray, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  Add,
  CloseOutlined,
  InfoOutlined
} from "@mui/icons-material";

import messages from "../../../../../../assets/locale/messages";
import Input from "../../../../../../components/Input";
import Tooltip from "../../../../../../components/Tooltip";
import FileUpload from "../../../../../../components/Upload/FileUpload";
import {
  BLOB_CONTAINERS,
  UploadBlobToContainer
} from "../../../../../../utils/blobStorageServices";
import { OFF_PLATFORM_VERIFICATION_DOCUMENTS_TYPES } from "../../../../../../utils/Constants";
import { sendRequiredVerificationDocumentsRequest } from "../../../../../../store/BusinessVerification/actions";

const UploadDocuments = ({ onValuesChange, onErrorsChange }) => {
  const documentsArrRef = useRef();
  const dispatch = useDispatch();

  const lang = useSelector((state) => state.locale.lang);
  const { businessVerification, shared } = messages[lang];

  const requestDetails = useSelector(
    (state) =>
      state.businessVerification.offPlatformVerificationRequestDetails
  );

  // Left commented as it might be needed again if requested
  //   Yup.addMethod(
  //     Yup.array,
  //     "unique",
  //     function (message, mapper = (a) => a) {
  //       return this.test("unique", message, function (list) {
  //         if (list) {
  //           const seen = {};
  //           const duplicates = {};

  //           // First pass to identify all duplicates
  //           list.forEach((item) => {
  //             const value = mapper(item)?.trim()?.toLowerCase();
  //             if (seen[value]) {
  //               duplicates[value] = true;
  //             } else {
  //               seen[value] = true;
  //             }
  //           });

  //           // Second pass to collect errors for all duplicates
  //           const errors = list
  //             .map((item, index) => {
  //               const value = mapper(item)?.trim()?.toLowerCase();
  //               if (duplicates[value]) {
  //                 return this.createError({
  //                   path: `${this.path}[${index}].name`,
  //                   message
  //                 });
  //               }
  //               return null;
  //             })
  //             .filter((error) => error !== null);

  //           if (errors.length > 0) {
  //             return new Yup.ValidationError(errors);
  //           }
  //         }
  //         return true;
  //       });
  //     }
  //   );

  const formik = useFormik({
    initialValues: {
      documents: [{ name: "" }],
      defaultDocument: null,
      defaultDocumentInfo: { name: "", type: "" }
    },
    validationSchema: Yup.object().shape(
      {
        documents: Yup.array().of(
          Yup.object().shape({
            name: Yup.string().max(50, "offplatformDocumentMaxLength")
          })
        ),
        // .unique("offplatformDocumentUnique", (item) => item.name),
        defaultDocument: Yup.mixed().nullable()
      },
      ["documents", "defaultDocument"]
    ),
    onSubmit: async ({
      documents,
      defaultDocument,
      defaultDocumentInfo
    }) => {
      let documentName = "";
      if (defaultDocument) {
        documentName = await UploadBlobToContainer(
          {
            blob: defaultDocument,
            ...defaultDocumentInfo
          },
          defaultDocumentInfo?.type?.includes("pdf")
            ? BLOB_CONTAINERS.pdfs
            : BLOB_CONTAINERS.images
        );
      }
      const docsArr = documents
        .filter((doc) => !!doc.name)
        .map((doc) => ({
          Id: 0,
          DocumentName: doc.name,
          DocumentTypeId:
            OFF_PLATFORM_VERIFICATION_DOCUMENTS_TYPES.text,
          RequestId: requestDetails?.Id,
          DefaultDocumentName: null,
          OriginalDocumentName: null
        }));
      if (defaultDocument && documentName) {
        docsArr.push({
          Id: 0,
          DefaultDocumentName: documentName,
          OriginalDocumentName: defaultDocumentInfo.name,
          DocumentTypeId: defaultDocumentInfo?.type?.includes("pdf")
            ? OFF_PLATFORM_VERIFICATION_DOCUMENTS_TYPES.pdf
            : OFF_PLATFORM_VERIFICATION_DOCUMENTS_TYPES.image,
          RequestId: requestDetails?.Id,
          DocumentName: null
        });
      }
      dispatch(
        sendRequiredVerificationDocumentsRequest({
          documents: [...docsArr],
          id: requestDetails?.Id
        })
      );
    }
  });

  const {
    values,
    setFieldTouched,
    setFieldValue,
    errors,
    touched,
    handleSubmit,
    setValues
  } = formik;

  useEffect(() => {
    onValuesChange(values);
  }, [values]);

  useEffect(() => {
    onErrorsChange(errors);
  }, [errors]);

  return (
    <div className="upload-off-platform-documents">
      <div className="pt-4">
        <FormikProvider value={formik}>
          <form
            noValidate
            onSubmit={handleSubmit}
            id="offplatform-add-documents-form">
            <div className="d-flex align-items-center justify-content-start mb-4">
              <h6 className="title-color fsize-16 fweight-600 mb-0 me-2">
                {
                  businessVerification.requestDetails
                    .offplatformRequest.requiredDocuments
                }
              </h6>
              <Tooltip
                className="text-secondary ms-md-2"
                title={
                  businessVerification.requestDetails
                    .offplatformRequest.addDocuments.docsHint
                }
                content={<InfoOutlined className="text-secondary" />}
              />
            </div>
            <div className="manual-documents-container mb-4">
              <p className="mb-4 light-text-color fsize-13">
                {
                  businessVerification.requestDetails
                    .offplatformRequest.addDocuments.typeDocsName
                }

                <span className="fsize-12 text-light-grey ms-2">
                  {
                    businessVerification.requestDetails
                      .offplatformRequest.addDocuments.maxFiles
                  }
                </span>
              </p>
              <div className="documents-container row">
                <div className="col-12 col-md-8 col-lg-7">
                  <FieldArray
                    name="documents"
                    render={(arrayHelpers) => {
                      documentsArrRef.current = arrayHelpers;
                      return values.documents?.map((_, index) => (
                        <div
                          key={`document-${index}`}
                          className={`row ${index < values.documents.length - 1 && "mb-4"}`}>
                          <div className="col-md-10 col-9">
                            <Input
                              required
                              label={
                                businessVerification.requestDetails
                                  .offplatformRequest.addDocuments
                                  .docName
                              }
                              placeholder={
                                businessVerification.requestDetails
                                  .offplatformRequest.addDocuments
                                  .docNamePlaceholder
                              }
                              onChange={(value) => {
                                if (!value || value.trim()) {
                                  const modifiedValue = value.replace(
                                    /\s\s+/g,
                                    " "
                                  );
                                  if (value[0] !== " ") {
                                    setFieldTouched(
                                      `documents[${index}].name`
                                    );
                                    setFieldValue(
                                      `documents[${index}].name`,
                                      modifiedValue
                                    );
                                  }
                                }
                              }}
                              value={values.documents[index].name}
                              isInputHasErr={
                                !!(
                                  touched?.documents?.[index]?.name &&
                                  errors?.documents?.[index]?.name
                                )
                              }
                              errMsg={
                                errors?.documents?.[index]?.name
                              }
                              name={`name-${index}`}
                              id={`name-${index}`}
                            />
                          </div>
                          <div className="action-btns col-md-2 col-3 d-flex  align-items-center">
                            {values.documents?.length > 1 && (
                              <span
                                className="pointer p-1 circular-border-radius grey-bg d-flex align-items-center justify-content-center"
                                onClick={() => {
                                  documentsArrRef.current.remove(
                                    index
                                  );
                                }}>
                                <CloseOutlined fontSize="small" />
                              </span>
                            )}
                            {values.documents?.length < 15 &&
                              values.documents?.length - 1 ===
                                index && (
                                <span
                                  className="pointer p-1 primary-border circular-border-radius d-flex align-items-center justify-content-center ms-4"
                                  onClick={() => {
                                    if (values.documents.length < 15)
                                      documentsArrRef.current.push({
                                        name: ""
                                      });
                                  }}>
                                  <Add
                                    fontSize="small"
                                    sx={{
                                      color: "primary.main"
                                    }}
                                  />
                                </span>
                              )}
                          </div>
                        </div>
                      ));
                    }}
                  />
                </div>
                {values.documents?.length >= 15 && (
                  <div className="col-12">
                    <p className="text-secondary mb-0">
                      {
                        businessVerification.requestDetails
                          .offplatformRequest.addDocuments
                          .docsMaxNumber
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="upload-documents-container">
              <p className="mb-3 light-text-color fsize-13">
                {
                  businessVerification.requestDetails
                    .offplatformRequest.addDocuments.uploadDocs
                }
              </p>
              <div className="documents">
                <FileUpload
                  name="defaultDocument"
                  onChange={(file) => {
                    setFieldTouched("defaultDocument");
                    setValues({
                      ...values,
                      defaultDocument: file,
                      defaultDocumentInfo: {
                        name: file?.name || "",
                        type: file?.type || ""
                      }
                    });
                  }}
                  value={values.defaultDocument}
                  fileInfo={values.defaultDocumentInfo}
                  required={true}
                  fileUploadLabel={shared.browseFiles}
                  acceptsImgs
                  isInputHasErr={
                    !!(
                      touched.defaultDocument &&
                      errors.defaultDocument
                    )
                  }
                  errMsg={errors.defaultDocument}
                />
              </div>
            </div>
          </form>
        </FormikProvider>
      </div>
    </div>
  );
};

export default UploadDocuments;

UploadDocuments.propTypes = {
  onValuesChange: Proptypes.func.isRequired,
  onErrorsChange: Proptypes.func.isRequired
};
